/**
 * Customer service tickets: the contract between the admin's Customer service
 * screens and the server (`admin/support-ticket`). The contact form creates
 * one; admins read, triage and reply. All times are epoch seconds.
 */

/** Why they wrote in: the contact form's reasons, plus reports from the site. */
export type SupportTicketReason =
  | 'general'
  | 'guest-support'
  | 'host-support'
  | 'press-media'
  | 'partnerships'
  | 'bug-report'
  | 'report-issue';

export const SUPPORT_TICKET_REASONS: { id: SupportTicketReason; label: string }[] = [
  { id: 'general', label: 'General question' },
  { id: 'guest-support', label: 'Guest / booking support' },
  { id: 'host-support', label: 'Host support' },
  { id: 'press-media', label: 'Press & media' },
  { id: 'partnerships', label: 'Partnerships' },
  { id: 'bug-report', label: 'Report a bug' },
  { id: 'report-issue', label: 'Report an issue' },
];

/** Open: waiting on us. Waiting: we replied and wait on the customer. Solved: done (reopens if they write again). */
export type SupportTicketStatus = 'open' | 'waiting' | 'solved';

export const SUPPORT_TICKET_STATUSES: { id: SupportTicketStatus; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'waiting', label: 'Waiting on customer' },
  { id: 'solved', label: 'Solved' },
];

export type SupportTicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export const SUPPORT_TICKET_PRIORITIES: { id: SupportTicketPriority; label: string }[] = [
  { id: 'urgent', label: 'Urgent' },
  { id: 'high', label: 'High' },
  { id: 'normal', label: 'Normal' },
  { id: 'low', label: 'Low' },
];

/** One message on a ticket: the customer's, or an admin's reply. */
export interface ISupportTicketMessage {
  id: number;
  ticketId: number;
  /** The admin who replied; null for the customer's own messages. */
  adminId?: number | null;
  /** Display name: the admin's name, or the requester's. */
  fromName?: string;
  body: string;
  /** True when it went to (or came in by) email. */
  viaEmail: boolean;
  created: number;
}

export interface ISupportTicket {
  id: number;
  /** What support and the customer quote: "T-1042". */
  number: string;
  reason: SupportTicketReason;
  subject: string;
  /** The first message, as the customer wrote it. */
  body: string;
  requesterName?: string;
  requesterEmail: string;
  /** Their account, when signed in or matched by email. */
  userId?: number | null;
  /** The booking it's about, when they picked one. */
  reservationId?: number | null;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  /** The admin handling it. */
  assigneeId?: number | null;
  assigneeName?: string | null;
  created: number;
  updated: number;
  /** When it last changed status to waiting or solved, or a reply went out; for "time waiting on us". */
  lastRepliedAt?: number | null;
  /** Only on `GET admin/support-ticket/:id`, oldest first. */
  messages?: ISupportTicketMessage[];
}

/** `GET admin/support-ticket` filters; every field optional. */
export interface ISupportTicketWhere {
  /** One status or several (`status=open,waiting` on the wire). */
  status?: SupportTicketStatus | SupportTicketStatus[];
  reason?: SupportTicketReason;
  /** An admin's id, or 'none' for unassigned. */
  assigneeId?: number | 'none';
  userId?: number;
  reservationId?: number;
  /** Matches number, subject, requester name and email. */
  search?: string;
  /** 1-based. */
  page?: number;
  pageSize?: number;
}

/** `GET admin/support-ticket`: newest update first. */
export interface ISupportTicketPage {
  tickets: ISupportTicket[];
  total: number;
}

/** `PATCH admin/support-ticket/:id`: any of these. */
export interface ISupportTicketUpdate {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  assigneeId?: number | null;
}

/** `POST admin/support-ticket/:id/reply`: emails the requester and adds the message. */
export interface ISupportTicketReply {
  body: string;
  /** Status after replying; usually 'waiting'. */
  status?: SupportTicketStatus;
}

/** `GET admin/support-ticket/counts`: for the menu badge and Home. */
export interface ISupportTicketCounts {
  open: number;
  waiting: number;
  /** Open and nobody assigned. */
  unassigned: number;
}
